// buildbotsnow.com/compare: what popular website chatbots cost, side by side,
// for people choosing between them (and for our "alternatives" ads). The
// facts and their sources are in lib/compare.ts; this page only lays them out.
//
// Public and prerendered, like the legal pages (src/prerender.tsx), so search
// engines and link previews read the text.

import { useEffect } from "react";
import { Link } from "react-router-dom";
import { PRODUCT_NAME } from "../lib/brand";
import { CHECKED_ON, DIFFERENCES, OURS, RIVALS, WHEN_THEY_FIT, checkedOnLabel, type Rival } from "../lib/compare";
import { ColorModeToggle } from "../components/ColorModeToggle";
import Footer from "../components/Footer";
import { Logo } from "../components/Logo";

export const COMPARE_TITLE = `AI website chatbot pricing compared | ${PRODUCT_NAME}`;

function Row({ rival, ours = false }: { rival: Rival; ours?: boolean }) {
  return (
    <tr className={ours ? "bg-brand-50/60" : undefined}>
      <th scope="row" className="py-3 pr-4 text-left align-top font-semibold text-slate-900">
        {ours ? rival.name : <a href={rival.pricingUrl} target="_blank" rel="noreferrer noopener nofollow" className="underline decoration-slate-300 hover:decoration-slate-500">{rival.name}</a>}
      </th>
      <td className="py-3 pr-4 align-top text-slate-700">{rival.free}</td>
      <td className="py-3 pr-4 align-top text-slate-700">{rival.entry}</td>
      <td className="py-3 pr-4 align-top text-slate-700">{rival.includes}</td>
      <td className="py-3 align-top text-slate-600">{rival.extras}</td>
    </tr>
  );
}

export default function Compare() {
  useEffect(() => {
    const title = document.title;
    document.title = COMPARE_TITLE;
    return () => {
      document.title = title;
    };
  }, []);

  const checked = checkedOnLabel();
  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="border-b border-slate-200 bg-surface">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/" aria-label={`${PRODUCT_NAME} home`} className="flex">
            <Logo />
          </Link>
          <ColorModeToggle />
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-10">
        <h1 className="text-3xl font-semibold text-slate-900 sm:text-4xl">AI website chatbots, priced side by side</h1>
        <p className="mt-3 max-w-3xl text-slate-600">
          Choosing an assistant that answers your customers from your own content? Here is what the popular ones charge a small business, taken
          from each company&apos;s own pricing page on {checked}. Each counts usage its own way (messages, credits, queries, conversations or
          outcomes), so every row uses that company&apos;s own unit.
        </p>

        <div className="mt-8 overflow-x-auto rounded-2xl border border-slate-200 bg-surface p-4">
          <table className="w-full min-w-[860px] text-left text-sm" aria-label="Pricing compared">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th scope="col" className="pb-2 pr-4 font-medium">Product</th>
                <th scope="col" className="pb-2 pr-4 font-medium">Free plan</th>
                <th scope="col" className="pb-2 pr-4 font-medium">First paid plan</th>
                <th scope="col" className="pb-2 pr-4 font-medium">What it includes</th>
                <th scope="col" className="pb-2 font-medium">Beyond that</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <Row rival={OURS} ours />
              {RIVALS.map((rival) => (
                <Row key={rival.name} rival={rival} />
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Prices in US dollars, monthly billing unless noted, as published on {checked} (
          <time dateTime={CHECKED_ON}>{CHECKED_ON}</time>). Prices change; each name links to its current pricing page. Names are the trademarks of
          their owners, who do not endorse this page.
        </p>

        <section className="mt-12">
          <h2 className="text-2xl font-semibold text-slate-900">What is different about {PRODUCT_NAME}</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {DIFFERENCES.map((item) => (
              <div key={item.title} className="rounded-2xl border border-slate-200 bg-surface p-5">
                <h3 className="font-semibold text-slate-900">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12 max-w-3xl">
          <h2 className="text-2xl font-semibold text-slate-900">When another tool may suit you better</h2>
          <div className="mt-4 space-y-3 text-slate-600">
            {WHEN_THEY_FIT.map((text) => (
              <p key={text}>{text}</p>
            ))}
          </div>
        </section>

        <section className="mt-12 rounded-2xl bg-brand-600 px-6 py-8 text-white">
          <h2 className="text-2xl font-semibold">Try it on your own content</h2>
          <p className="mt-2 max-w-2xl text-brand-50">Paste your website and see your assistant answer before you sign up. Free, no card.</p>
          <Link to="/start" className="mt-5 inline-block rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-brand-700 hover:bg-brand-50">
            Build your bot free
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
}
